/**
 * Vista de detalle de un comprobante traído desde ACSIS — datos REALES.
 *
 * Muestra header (n° AFIP, fechas, cliente, transporte), items y partidas
 * (las líneas trazables con N°serie / lote / vencimiento). El "Trazar"
 * (escaneo, evento ANMAT, cumplir-tarea) viene como próximo paso.
 */

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Zap,
  AlertTriangle,
  FileDown,
  RefreshCw,
  CheckCircle2,
  ChevronRight,
  ScanLine,
  Trash2,
} from 'lucide-react';
import { toast } from '@/lib/notify';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  useComprobante,
  useComprobanteRaw,
  useIniciarTrazaMutation,
  useCumplirTareaMutation,
  useVincularProductVarMutation,
  useResincronizarDespachoMutation,
  useVerificarCambiosMutation,
  useSetGlnDestinoMutation,
  useAsignarDisponiblesMutation,
  useCancelarEgresoMutation,
  useCancelarRemitoMutation,
  useTrazaJobByReferencia,
  useClienteAcsis,
  useGlnSnt,
  useEventosDisponibles,
  usePreviewPayloadEgreso,
  useUbicaciones,
} from '@/api/queries';
import { useSessionStore } from '@/stores/session';
import type { TareaPendiente } from '@/api/contracts/tareas';
import type { TrazaJobDetalle } from '@/api/contracts/traza-job-detalle';
import type { ComprobanteDetalle } from '@/api/contracts/comprobantes';
import type { ComprobanteTipo } from '@/api/endpoints/comprobantes';
import { generarPdfTrazaJob } from '@/lib/trazabilidad-job-pdf';
import { CumplirTareaChip } from './CumplirTareaChip';
import { usePickeoLotes, type UsePickeoLotesResult } from './usePickeoLotes';
import { PickeoModal } from './PickeoModal';
import { ConfirmarQuitarPickeoDialog } from './ConfirmarQuitarPickeoDialog';
import { CancelarEgresoDialog } from './CancelarEgresoDialog';
import type { DestinoAnulacionEgreso } from '@/api/endpoints/comprobantes';
import { claveGrupo, esPartidaPorLote, unidadesDePartida } from './pickeo-lotes';
import { TrazaProgreso } from './TrazaProgreso';

/** Referencia estable para el hook de pickeo mientras el detalle no cargó. */
const SIN_PARTIDAS: ComprobanteDetalle['partidas'] = [];

/**
 * Pickeo por escaneo (botón "Pickear", modal y columna "Pickeo"). Oculto por
 * pedido del cliente hasta que se defina el resto del flujo; el código queda
 * completo. Poner en `true` para volver a mostrarlo.
 */
const PICKEO_HABILITADO = true as boolean;

/** Color del chip por movimiento (prefijo de referencia): salida azul, destrucción roja. */
const BADGE_BY_MOVIMIENTO: Record<'salida' | 'destruccion', string> = {
  salida: 'badge-blue',
  destruccion: 'badge-red',
};

/** "2026-05-27 00:00:00.000" → "27/05/2026". */
function fmtFechaAcsis(value: string | null | undefined): string {
  if (!value) return '—';
  const [y, m, d] = value.slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : value;
}

/**
 * Es íntegro si TODAS las partidas tienen un `stockId` asignado. Una sola
 * partida sin stock indica que el sync no completó el seed de inventario
 * para ese comprobante — no se puede trazar hasta resincronizar.
 *
 * Si no hay partidas todavía (loading), tratamos como íntegro (no mostrar
 * alerta) para no hacer ruido durante la carga inicial.
 */
function esIntegro(data: ComprobanteDetalle | undefined | null): boolean | null {
  if (!data) return null;
  return partidasConStockEsperado(data).every((p) => p.stockId != null);
}

function contarSinStock(data: ComprobanteDetalle): number {
  return partidasConStockEsperado(data).filter((p) => p.stockId == null).length;
}

/**
 * Partidas que ya deberían tener stock desde el sync. Las partidas por lote
 * (sin serie) quedan afuera: su stock se crea recién al pickear las unidades.
 */
function partidasConStockEsperado(data: ComprobanteDetalle) {
  return data.partidas.filter((p) => !esPartidaPorLote(p));
}

/**
 * Motivo por el que el pickeo no deja trazar (remitos por lote: pickeo
 * obligatorio y completo), o null. La regla la aplica el back; acá solo se
 * muestra lo que informa el detalle.
 */
function bloqueoPorPickeo(data: ComprobanteDetalle): string | null {
  const t = data.pickeoTraza;
  return t?.requerido && !t.completo ? (t.motivo ?? 'Falta completar el pickeo.') : null;
}

/**
 * SKU de los renglones (items) SIN GTIN cargado en el ERP. Un artículo sin
 * `codigoDeBarras` no se puede trazar (riga lo rebota con error 3000), así que
 * bloqueamos la traza en el front. El GTIN viene resuelto por SKU en `items`.
 */
function skusSinGtin(data: ComprobanteDetalle | undefined | null): string[] {
  if (!data) return [];
  return [
    ...new Set(
      data.items
        // Solo los TRAZABLES (trazable === true) van a ANMAT y necesitan GTIN.
        // Los no trazables (false) Y los no resueltos en el maestro (null) NO
        // bloquean — a un no-trazable no se le exige GTIN. Si alguno resultara
        // trazable sin GTIN, el backend lo frena igual (GTIN_FALTANTE).
        .filter((it) => it.trazable === true && !it.gtin)
        .map((it) => it.sku ?? '(sin SKU)'),
    ),
  ];
}

/** ISO o "YYYY-MM-DD HH:MM:SS" → "DD/MM/YYYY HH:MM". '—' si no parsea. */
function fmtFechaHora(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, '0');
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mo}/${yy} ${hh}:${mm}`;
}

interface Props {
  tarea: TareaPendiente;
  onVolver: () => void;
  /** true cuando se abre desde la solapa "Completadas" — el despacho ya se
   *  trazó, así que ocultamos acciones de sincronización (Resincronizar). */
  esCompletada?: boolean;
}

export function ComprobanteDetalleView({ tarea, onVolver, esCompletada = false }: Props) {
  // El tipo "otro" no tiene endpoint de detalle — bloqueamos en UI.
  const tipoSoportado: ComprobanteTipo | null =
    tarea.tipo === 'otro' ? null : tarea.tipo;

  const { data, isLoading, error } = useComprobante(
    tipoSoportado ? tarea.referencia : null,
    tipoSoportado,
  );

  // Pickeo por escaneo. El estado vive acá (no en el modal) para que los tildes
  // se conserven al cerrar el modal y se vean también en la tabla de partidas.
  const pickeo = usePickeoLotes(data?.partidas ?? SIN_PARTIDAS, {
    referencia: tarea.referencia,
    guardados: data?.pickeos,
  });

  // GLN origen (sesión actual) y destino (guardado en el sync — se lee de la
  // DB, no del ERP en vivo). El botón "Resincronizar" lo actualiza.
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);
  const { data: ubicaciones = [] } = useUbicaciones();
  const glnOrigen = ubicaciones.find((u) => u.id === ubicacionActivaId)?.gln ?? null;
  const glnDestino = data?.glnDestino ?? null;

  // Tipo agente SNT: se consulta EN VIVO al SNT (ANMAT) por el GLN destino
  // (GET /api/snt/gln/:gln → nombreTipoAgente). No se guarda. Si cambia el GLN
  // destino (se configura/edita), React Query re-consulta solo (query por gln).
  const glnSntQuery = useGlnSnt(glnDestino);

  // Estado de la traza de egreso (comparte la query/poll con TrazarSection —
  // React Query la deduplica por key). Una vez trazado con ÉXITO el comprobante
  // queda "congelado": no se puede cambiar el evento ni editar el GLN destino.
  // Cubre tanto el caso ya completado (`esCompletada`) como el instante justo
  // después de trazar (cuando todavía no migró a la solapa Completadas).
  const trazaJobQuery = useTrazaJobByReferencia(tarea.referencia ?? null, 'egreso');
  // `anulada` (egreso cancelado después de una traza exitosa) con el remito
  // anulado se muestra como la traza realizada: el comprobante sigue congelado.
  // Anulada para volver a trazar (remito sin anular) vuelve a ser pendiente.
  const esTrazaHecha = (st: string | null | undefined) =>
    st === 'success' || (st === 'anulada' && Boolean(data?.remitoAnulado));
  const yaTrazadoOk =
    esCompletada || esTrazaHecha(trazaJobQuery.data?.status) || esTrazaHecha(tarea.traza?.status);
  // Traza en curso (pending/running): tampoco se resincroniza — no pisar datos
  // mientras la traza está corriendo.
  const trazaStatus = trazaJobQuery.data?.status ?? tarea.traza?.status ?? null;
  const trazaEnCurso = trazaStatus === 'pending' || trazaStatus === 'running';
  // Con la traza en curso o terminada, el pickeo no se modifica (el back lo
  // rechaza): no se ofrece quitar series.
  const pickeoBloqueado = yaTrazadoOk || trazaEnCurso;

  // Eventos ANMAT disponibles para (origen = GLN activo, destino = GLN destino).
  // Solo tiene sentido consultarlo para un despacho pendiente y soportado con
  // GLN destino cargado. Por ahora (paso 1) solo poblamos el combo; el evento
  // elegido todavía NO se usa para trazar.
  const eventosQuery = useEventosDisponibles(
    tipoSoportado ? tarea.referencia : null,
    ubicacionActivaId,
    glnDestino,
    Boolean(tipoSoportado && !esCompletada && glnDestino),
  );
  const [eventoElegido, setEventoElegido] = useState<string>('');

  // Bloqueo de traza por el evento SNT. null = OK para trazar. Cualquier string
  // = motivo por el que NO se puede trazar todavía (se muestra y deshabilita el
  // botón). Ahora el evento es SIEMPRE obligatorio: si el combo no ofrece
  // ninguno es un error de datos (destino/origen sin tipo de actor, par sin
  // evento en el catálogo) → NO se traza (ya no cae al 133 por default).
  const eventoBloqueoMsg: string | null = (() => {
    if (!tipoSoportado || yaTrazadoOk) return null;
    if (!glnDestino) return null; // ya lo bloquea el aviso "falta GLN destino"
    if (eventosQuery.isLoading) return 'Cargando eventos SNT…';
    if (eventosQuery.error) return 'No se pudieron cargar los eventos SNT.';
    const evs = eventosQuery.data?.eventos ?? [];
    if (evs.length === 0) {
      const m = eventosQuery.data?.motivo;
      return m === 'DESTINO_SIN_TIPO_ACTOR'
        ? 'El GLN destino no está registrado en el SNT — no hay eventos para trazar.'
        : m === 'DESTINO_TIPO_ACTOR_NO_MAPEADO'
          ? 'El tipo de actor del GLN destino no está mapeado en agent_types — no se puede trazar.'
          : m === 'ORIGEN_SIN_TIPO_ACTOR'
            ? 'El GLN de origen no tiene tipo de actor configurado — no hay eventos para trazar.'
            : 'No hay eventos SNT para este origen/destino — no se puede trazar (revisá los tipos de actor).';
    }
    if (!eventoElegido) return 'Elegí el evento SNT en el combo del encabezado.';
    return null;
  })();

  // Descripción del evento elegido — se snapshotea al trazar (se manda al back).
  const eventoDescElegido =
    eventosQuery.data?.eventos.find((e) => String(e.eventCode) === eventoElegido)?.descripcion ??
    null;
  // Evento con el que YA se trazó (para mostrarlo read-only). Sale del propio
  // job (code + desc persistidos), sin depender del combo ni del GLN activo.
  const trazaEventCode = trazaJobQuery.data?.eventCode ?? null;
  const trazaEventDesc = trazaJobQuery.data?.eventDesc ?? null;

  const [showAcsisLog, setShowAcsisLog] = useState(false);
  const [showResync, setShowResync] = useState(false);
  const [showGlnModal, setShowGlnModal] = useState(false);
  const resyncMutation = useResincronizarDespachoMutation();
  // El GLN destino se puede cargar/editar a mano solo si el comprobante es
  // soportado (tipo válido), tiene referencia y NO está ya trazado (completadas).
  const puedeEditarGln = Boolean(tarea.referencia && tipoSoportado && !yaTrazadoOk);

  const handleResincronizar = () => {
    if (!tarea.referencia || !tipoSoportado) return;
    resyncMutation.mutate(
      { referencia: tarea.referencia, tipo: tipoSoportado },
      {
        onSuccess: (res) => {
          setShowResync(false);
          const glnTxt = res.glnDestino
            ? `GLN destino ${res.glnDestino}`
            : 'sin GLN destino';
          const gtinTxt = `${res.gtinResueltos}/${res.skusTotal} GTIN`;
          const depoTxt = ` · ${res.depositosResueltos ?? 0}/${res.skusTotal} depósito`;
          const st = res.stock;
          const stockTxt = st
            ? ` · stock: +${st.agregadas} / -${st.canceladas}` +
              (st.conflictos > 0 ? ` · ${st.conflictos} a reasignar` : '')
            : '';
          const faltan =
            res.sinGtin.length > 0 ? ` · faltan GTIN: ${res.sinGtin.join(', ')}` : '';
          const msg = `Resincronización finalizada · ${glnTxt} · ${gtinTxt}${depoTxt}${stockTxt}${faltan}`;
          // Un solo cartel: verde si quedó todo resuelto, rojo si falta algo.
          const completo = Boolean(res.glnDestino) && res.sinGtin.length === 0;
          if (completo) toast.success(msg);
          else toast.error(msg);
        },
        onError: (err) => {
          toast.error(
            `No se pudo resincronizar: ${err instanceof Error ? err.message : String(err)}`,
          );
        },
      },
    );
  };

  // Backfill de product_var: hay stock del despacho vinculado a esta tarea pero
  // sin `product_var_id` (ej. sincronizado antes de tener el maestro). El botón
  // resuelve/crea el product_var desde el ERP por SKU y updatea el stock.
  const vincularMutation = useVincularProductVarMutation();
  const hayStockSinProductVar = Boolean(
    data?.partidas.some((p) => p.stockId != null && p.productVarId == null),
  );
  const handleVincularProductVar = () => {
    if (!tarea.referencia || !tipoSoportado) return;
    vincularMutation.mutate(
      { referencia: tarea.referencia, tipo: tipoSoportado },
      {
        onSuccess: (res) => {
          if (res.vinculadas > 0) {
            toast.success(`${res.vinculadas} serie(s) vinculada(s) al maestro de artículos`);
          } else if (res.total === 0) {
            toast.success('No había stock pendiente de vincular');
          }
          if (res.sinResolver.length > 0) {
            toast.error(
              `Sin resolver en el ERP: ${res.sinResolver.join(', ')}`,
            );
          }
        },
        onError: (err) => {
          toast.error(`No se pudo vincular: ${err instanceof Error ? err.message : String(err)}`);
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onVolver}
          className="inline-flex items-center gap-1.5 text-xs text-gray-600 transition hover:text-everex-azul"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Volver al listado
        </button>
        {!yaTrazadoOk && !trazaEnCurso && tipoSoportado && data && tarea.referencia && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowResync(true)}
            disabled={resyncMutation.isPending}
            className="text-xs"
            title="Resincronizar GLN destino y GTIN contra el ERP (no toca el stock)"
          >
            {resyncMutation.isPending ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="mr-1 h-3.5 w-3.5" />
            )}
            {resyncMutation.isPending ? 'Resincronizando…' : 'Resincronizar'}
          </Button>
        )}
      </div>

      <ResincronizarDialog
        open={showResync}
        onOpenChange={setShowResync}
        onConfirm={handleResincronizar}
        pending={resyncMutation.isPending}
      />

      {puedeEditarGln && tipoSoportado && tarea.referencia && (
        <GlnDestinoDialog
          open={showGlnModal}
          onOpenChange={setShowGlnModal}
          referencia={tarea.referencia}
          tipo={tipoSoportado}
          valorActual={glnDestino}
        />
      )}

      {/* Trazar al SNT — botón + estado del último intento. Va arriba para que
          la acción primaria quede en la primera pantalla sin scrollear.
          Si el comprobante no es íntegro o el cliente no tiene GLN destino
          cargado en el ERP, se deshabilita el trazar. */}
      {tipoSoportado && data && tarea.referencia && (
        <TrazarSection
          tarea={tarea}
          tipo={tipoSoportado}
          comprobante={data}
          deshabilitadoPorIntegridad={esIntegro(data) === false}
          pickeoBloqueoMsg={bloqueoPorPickeo(data)}
          glnOrigen={glnOrigen}
          glnDestino={glnDestino}
          tipoAgenteDestino={glnSntQuery.data?.tipoAgente ?? null}
          skusSinGtin={skusSinGtin(data)}
          idEvento={eventoElegido ? Number(eventoElegido) : null}
          eventoDesc={eventoDescElegido}
          eventoBloqueoMsg={eventoBloqueoMsg}
          pickeo={pickeo}
        />
      )}

      {/* Header del comprobante */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn('badge', BADGE_BY_MOVIMIENTO[tarea.movimiento ?? 'salida'])}
              title={tarea.tipoLabel}
            >
              <b>{tarea.sigla}</b>
            </span>
            <CardTitle className="font-mono text-everex-azul">
              {data?.numeroAfip ?? tarea.numeroRemito ?? tarea.referencia ?? '—'}
            </CardTitle>
          </div>
          {tarea.asunto && <p className="mt-1 text-xs text-gray-600">{tarea.asunto}</p>}
        </CardHeader>
        <CardContent>
          {!tipoSoportado ? (
            <Alert>
              <AlertDescription className="text-xs">
                Este tipo de comprobante (claseComprobante {tarea.claseComprobante ?? '?'}) no
                tiene endpoint de detalle implementado.
              </AlertDescription>
            </Alert>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-8 text-gray-600">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              <span>Cargando comprobante...</span>
            </div>
          ) : error ? (
            <div className="space-y-3">
              <Alert variant="destructive">
                <AlertDescription>
                  No se pudo traer el detalle:{' '}
                  {error instanceof Error ? error.message : String(error)}
                </AlertDescription>
              </Alert>
              {/* Reparación puntual: vuelve a pedir el detalle de ESTE comprobante
                  al ERP y lo persiste, sin correr el sync completo del GLN. Si el
                  ERP sigue sin devolverlo, el toast de error muestra el motivo real
                  de ACSIS (en vez del genérico "volvé a sincronizar"). */}
              {!yaTrazadoOk && !trazaEnCurso && tipoSoportado && tarea.referencia && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResincronizar}
                  disabled={resyncMutation.isPending}
                  className="text-xs"
                  title="Volver a pedir el detalle de este comprobante al ERP y persistirlo"
                >
                  {resyncMutation.isPending ? (
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-1 h-3.5 w-3.5" />
                  )}
                  {resyncMutation.isPending
                    ? 'Resincronizando…'
                    : 'Resincronizar este comprobante'}
                </Button>
              )}
            </div>
          ) : data ? (
            <div className="grid grid-cols-2 gap-4 text-xs md:grid-cols-4">
              <Dato label="Referencia ERP" mono>
                {tarea.referencia ?? '—'}
              </Dato>
              <Dato label="ID ERP" mono>
                {tarea.id ?? '—'}
                {tipoSoportado && tarea.referencia && (
                  <button
                    type="button"
                    onClick={() => setShowAcsisLog(true)}
                    className="ml-2 font-sans text-[10px] font-normal text-gray-500 underline-offset-2 transition hover:text-everex-azul hover:underline"
                    title="Ver el JSON crudo devuelto por el ERP"
                  >
                    (ver log)
                  </button>
                )}
              </Dato>
              <Dato label="ID Everex Traza" mono>
                {data.localId ?? '—'}
              </Dato>
              <Dato label="Fecha de emisión">{fmtFechaAcsis(data.comprobante.fechaEmision)}</Dato>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  GLN origen → destino
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-gray-800">
                  {glnOrigen ? (
                    <span className="font-mono">{glnOrigen}</span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                  <ArrowRight className="h-3.5 w-3.5 text-gray-400" />
                  {glnDestino ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="font-mono">{glnDestino}</span>
                      {puedeEditarGln && (
                        <button
                          type="button"
                          onClick={() => setShowGlnModal(true)}
                          className="font-sans text-[10px] font-normal text-gray-500 underline-offset-2 transition hover:text-everex-azul hover:underline"
                          title="Editar GLN destino"
                        >
                          (editar)
                        </button>
                      )}
                    </span>
                  ) : puedeEditarGln ? (
                    <button
                      type="button"
                      onClick={() => setShowGlnModal(true)}
                      className="italic text-everex-azul underline underline-offset-2 transition hover:text-everex-azul-hover"
                      title="Cargar GLN destino"
                    >
                      configurar
                    </button>
                  ) : (
                    <span className="text-gray-400 italic">sin configurar</span>
                  )}
                </div>
              </div>
              {/* Tipo agente SNT: consulta EN VIVO al SNT por el GLN destino.
                  Sin GLN → no hay qué consultar; consultando → "Consultando en
                  SNT…"; no encontrado → aviso; error → aviso. No se guarda. */}
              <Dato label="Tipo agente SNT">
                {!glnDestino ? (
                  <span className="text-gray-400 italic">sin GLN destino</span>
                ) : glnSntQuery.isLoading ? (
                  <span className="text-gray-400 italic">Consultando en SNT…</span>
                ) : glnSntQuery.error ? (
                  <span className="text-gray-400 italic">error consultando el SNT</span>
                ) : glnSntQuery.data?.tipoAgente ? (
                  glnSntQuery.data.tipoAgente
                ) : (
                  <span className="text-gray-400 italic">no encontrado en el SNT</span>
                )}
              </Dato>
              {/* Evento SNT: combo con los eventos ANMAT posibles para el par
                  (actor origen = GLN activo, actor destino = "Tipo agente SNT" de
                  arriba). Se resuelve contra el catálogo de riga. Solo para
                  despachos pendientes/soportados. Paso 1: solo se muestra/elige;
                  todavía NO se usa para disparar la traza. */}
              {tipoSoportado && (
                <Dato label="Evento SNT" className="md:col-span-2">
                  {yaTrazadoOk ? (
                    // Ya trazado: mostramos el evento usado (read-only). El code y
                    // la desc salen del propio job (persistidos), así funciona
                    // también en Completadas e histórico, sin depender del combo.
                    trazaEventCode != null ? (
                      <span>
                        <span className="font-mono">{trazaEventCode}</span>
                        {trazaEventDesc ? ` — ${trazaEventDesc}` : ''}
                      </span>
                    ) : (
                      <span className="text-gray-400 italic">—</span>
                    )
                  ) : !glnDestino ? (
                    <span className="text-gray-400 italic">sin GLN destino</span>
                  ) : eventosQuery.isLoading ? (
                    <span className="text-gray-400 italic">Cargando eventos…</span>
                  ) : eventosQuery.error ? (
                    <span className="text-gray-400 italic">error consultando eventos</span>
                  ) : (eventosQuery.data?.eventos.length ?? 0) === 0 ? (
                    <span className="text-gray-400 italic">
                      {eventosQuery.data?.motivo === 'DESTINO_SIN_TIPO_ACTOR'
                        ? 'destino no registrado en SNT'
                        : eventosQuery.data?.motivo === 'DESTINO_TIPO_ACTOR_NO_MAPEADO'
                          ? 'tipo de actor del destino no reconocido'
                          : eventosQuery.data?.motivo === 'ORIGEN_SIN_TIPO_ACTOR'
                            ? 'origen sin tipo de actor'
                            : 'sin eventos disponibles'}
                    </span>
                  ) : (
                    <Select value={eventoElegido} onValueChange={setEventoElegido}>
                      <SelectTrigger className="h-7 w-full text-xs md:max-w-lg">
                        <SelectValue placeholder="Elegí un evento…" />
                      </SelectTrigger>
                      <SelectContent>
                        {(eventosQuery.data?.eventos ?? []).map((ev) => (
                          <SelectItem
                            key={ev.eventCode}
                            value={String(ev.eventCode)}
                            className="text-xs"
                          >
                            {ev.eventCode} — {ev.descripcion}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Dato>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Modal con el JSON crudo de ACSIS (lazy: solo dispara request si se abre). */}
      {tipoSoportado && tarea.referencia && (
        <AcsisRawDialog
          open={showAcsisLog}
          onOpenChange={setShowAcsisLog}
          referencia={tarea.referencia}
          tipo={tipoSoportado}
        />
      )}

      {/* Cliente (destino del despacho) — proxy normalizado desde ACSIS.
          Sirve para entender contra qué actor SNT vamos a trazar y qué GLN
          destino corresponde según el `renglonDomicilio` del comprobante. */}
      {data?.comprobante.clienteId && (
        <ClienteSection
          clienteId={data.comprobante.clienteId}
          renglonDomicilio={data.comprobante.renglonDomicilio}
        />
      )}

      {/* Items (renglones del comprobante) */}
      {data && (
        <Card>
          <CardHeader>
            <CardTitle className="text-everex-azul text-base">
              Renglones <span className="text-gray-500">({data.items.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.items.length === 0 ? (
              <p className="py-4 text-center text-xs text-gray-500">Sin items.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
                    <tr>
                      <th className="px-2 py-2 text-left">Renglón</th>
                      <th className="px-2 py-2 text-left">SKU</th>
                      <th className="px-2 py-2 text-left">Artículo</th>
                      <th className="px-2 py-2 text-left">GTIN</th>
                      <th className="px-2 py-2 text-right">Cantidad</th>
                      <th className="px-2 py-2 text-left">UM</th>
                      <th className="px-2 py-2 text-left">Depósito</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-800">
                    {data.items.map((it, i) => (
                      <tr key={`${it.sku ?? '?'}-${i}`} className="border-t border-gray-100">
                        <td className="px-2 py-2">{it.renglon ?? '—'}</td>
                        <td className="px-2 py-2 font-mono">{it.sku ?? '—'}</td>
                        <td className="px-2 py-2">
                          {it.nombre ?? it.descripcion ?? '—'}
                          {it.trazable && (
                            <span className="ml-1.5 badge badge-green">trazable</span>
                          )}
                        </td>
                        <td className="px-2 py-2 font-mono">
                          {it.gtin ?? (
                            <span className="text-gray-400 italic">sin código</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right">{it.cantidad ?? '—'}</td>
                        <td className="px-2 py-2">{it.unidadDeMedida ?? '—'}</td>
                        <td className="px-2 py-2">
                          {it.depositoNombre ?? it.deposito ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Partidas (las líneas trazables) */}
      {data && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <CardTitle className="text-everex-azul text-base">
                  Partidas / series <span className="text-gray-500">({data.partidas.length})</span>
                  {PICKEO_HABILITADO && pickeo.resumen.esperadas > 0 && (
                    <span className="badge badge-green ml-2 align-middle font-normal">
                      Pickeadas {pickeo.resumen.pickeadas} de {pickeo.resumen.esperadas}
                    </span>
                  )}
                </CardTitle>
                <p className="text-[11px] text-gray-600">
                  Líneas trazables del comprobante: lote, N° de serie y vencimiento.
                </p>
              </div>
              {hayStockSinProductVar && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleVincularProductVar}
                  disabled={vincularMutation.isPending}
                  className="text-xs"
                  title="Vincular el stock de este remito al maestro de artículos (product_var), creándolo desde el ERP si falta"
                >
                  {vincularMutation.isPending ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : null}
                  Vincular artículos
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {data.partidas.length === 0 ? (
              <p className="py-4 text-center text-xs text-gray-500">
                Este comprobante no tiene partidas con series. Si esperabas series, verificá con
                ACSIS la configuración del artículo (clasificador5 = trazable).
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
                    <tr>
                      {/* Chevron que despliega las series pickeadas de un lote. */}
                      {PICKEO_HABILITADO && <th className="w-6 py-2 pl-2" aria-hidden />}
                      <th className="px-2 py-2 text-left">SKU</th>
                      <th className="px-2 py-2 text-left">Artículo</th>
                      <th className="px-2 py-2 text-left">Lote</th>
                      <th className="px-2 py-2 text-left">N° de serie</th>
                      <th className="px-2 py-2 text-left">Vencimiento</th>
                      <th className="px-2 py-2 text-right">Cantidad</th>
                      <th
                        className="px-2 py-2 text-left"
                        title="erp_task_detail.id"
                      >
                        ID (Everex Traza)
                      </th>
                      <th
                        className="px-2 py-2 text-left"
                        title="stock.id (LEFT JOIN por erp_task_detail_id)"
                      >
                        ID Stock (Everex Traza)
                      </th>
                      {PICKEO_HABILITADO && (
                        <th className="px-2 py-2 text-right" title="Unidad pickeada por escaneo">
                          Pickeo
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="text-gray-800">
                    {data.partidas.map((p, i) => {
                      if (esPartidaPorLote(p)) {
                        return (
                          <FilasPartidaPorLote
                            key={`${p.id ?? p.lote ?? '?'}-${i}`}
                            partida={p}
                            pickeo={pickeo}
                            bloqueado={pickeoBloqueado}
                            primeraDelGrupo={
                              data.partidas.findIndex(
                                (x) => esPartidaPorLote(x) && claveGrupo(x) === claveGrupo(p),
                              ) === i
                            }
                          />
                        );
                      }
                      const pickeada =
                        Boolean(p.numeroDeSerie) &&
                        pickeo.seriesPickeadas.has((p.numeroDeSerie ?? '').trim());
                      return (
                        <tr
                          key={`${p.id ?? p.numeroDeSerie ?? p.lote ?? '?'}-${i}`}
                          className={cn('border-t border-gray-100', pickeada && 'bg-green-50/70')}
                        >
                          {PICKEO_HABILITADO && <td className="py-2 pl-2" />}
                          <td className="px-2 py-2 font-mono">{p.sku ?? '—'}</td>
                          <td className="px-2 py-2">{p.nombre ?? '—'}</td>
                          <td className="px-2 py-2 font-mono">{p.lote ?? '—'}</td>
                          <td className="px-2 py-2 font-mono">{p.numeroDeSerie ?? '—'}</td>
                          <td className="px-2 py-2">{fmtFechaAcsis(p.fechaVencimiento)}</td>
                          <td className="px-2 py-2 text-right">
                            {p.cantidad ?? p.cantidadInicial ?? '—'}
                          </td>
                          <td className="px-2 py-2 font-mono text-gray-500">{p.id ?? '—'}</td>
                          <td className="px-2 py-2 font-mono text-gray-500">{p.stockId ?? '—'}</td>
                          {PICKEO_HABILITADO && (
                            <td className="px-2 py-2 text-right">
                              {pickeada && (
                                <EstadoSeriePickeada
                                  serie={(p.numeroDeSerie ?? '').trim()}
                                  pickeo={pickeo}
                                  bloqueado={pickeoBloqueado}
                                />
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * Partida agrupada por lote (sin serie) en la tabla de partidas: una fila con
 * el lote y su cantidad, y debajo una fila por cada serie pickeada de ese
 * lote. A medida que se pickea, la tabla va quedando como en el formato por
 * serie.
 *
 * Si varias partidas caen en el mismo grupo (lote, artículo), las series
 * pickeadas se muestran solo debajo de la primera (`primeraDelGrupo`), para
 * no repetirlas. El ID de stock aparece cuando el back confirmó la unidad.
 *
 * Las series pickeadas arrancan colapsadas: el chevron de la fila del lote
 * las despliega.
 */
function FilasPartidaPorLote({
  partida: p,
  pickeo,
  bloqueado,
  primeraDelGrupo,
}: {
  partida: ComprobanteDetalle['partidas'][number];
  pickeo: UsePickeoLotesResult;
  /** Traza en curso o terminada: las series no se pueden quitar. */
  bloqueado: boolean;
  primeraDelGrupo: boolean;
}) {
  const key = claveGrupo(p);
  const esperadas = pickeo.grupos.find((g) => g.key === key)?.cantidad ?? 0;
  const pickeos = key ? pickeo.state.pickeos.filter((x) => x.grupoKey === key) : [];
  const completo = esperadas > 0 && pickeos.length >= esperadas;
  const [expandido, setExpandido] = useState(false);
  const desplegable = PICKEO_HABILITADO && primeraDelGrupo && pickeos.length > 0;
  const tituloChevron = expandido ? 'Ocultar las series pickeadas' : 'Ver las series pickeadas';

  return (
    <>
      <tr className={cn('border-t border-gray-100', completo && 'bg-green-50/70')}>
        {PICKEO_HABILITADO && (
          <td className="py-2 pl-2">
            {desplegable && (
              <button
                type="button"
                onClick={() => setExpandido((v) => !v)}
                className="flex rounded text-gray-500 hover:text-everex-azul focus:outline-none focus-visible:ring-2 focus-visible:ring-everex-azul/30"
                title={tituloChevron}
                aria-label={tituloChevron}
                aria-expanded={expandido}
              >
                <ChevronRight
                  className={cn('h-4 w-4 transition-transform', expandido && 'rotate-90')}
                />
              </button>
            )}
          </td>
        )}
        <td className="px-2 py-2 font-mono">{p.sku ?? '—'}</td>
        <td className="px-2 py-2">{p.nombre ?? '—'}</td>
        <td className="px-2 py-2 font-mono">{p.lote ?? '—'}</td>
        <td className="px-2 py-2" />
        <td className="px-2 py-2">{fmtFechaAcsis(p.fechaVencimiento)}</td>
        <td className="px-2 py-2 text-right">{unidadesDePartida(p)}</td>
        <td className="px-2 py-2 font-mono text-gray-500">{p.id ?? '—'}</td>
        <td className="px-2 py-2 font-mono text-gray-500">—</td>
        {PICKEO_HABILITADO && (
          <td
            className={cn(
              'px-2 py-2 text-right font-mono',
              completo ? 'text-green-700' : 'text-gray-600',
            )}
            title="Unidades pickeadas del lote / esperadas"
          >
            {primeraDelGrupo ? `${pickeos.length} / ${esperadas}` : ''}
          </td>
        )}
      </tr>
      {desplegable &&
        expandido &&
        pickeos.map((x) => (
          <tr key={x.serie} className="border-t border-gray-50">
            {/* SKU, artículo y lote son los de la fila del lote: no se repiten. */}
            <td className="py-1.5 pl-2" />
            <td className="px-2 py-1.5" />
            <td className="px-2 py-1.5" />
            <td className="px-2 py-1.5" />
            <td className="px-2 py-1.5 font-mono">{x.serie}</td>
            <td className="px-2 py-1.5">{fmtFechaAcsis(p.fechaVencimiento)}</td>
            <td className="px-2 py-1.5 text-right">1</td>
            <td className="px-2 py-1.5 font-mono text-gray-500">—</td>
            <td className="px-2 py-1.5 font-mono text-gray-500">{x.stockId ?? '…'}</td>
            <td className="px-2 py-1.5 text-right">
              <EstadoSeriePickeada
                serie={x.serie}
                pickeo={pickeo}
                bloqueado={bloqueado}
                conTilde={false}
              />
            </td>
          </tr>
        ))}
    </>
  );
}

/**
 * Celda "Pickeo" de una serie pickeada: tilde verde y tachito para quitarla
 * del pickeo, con confirmación (ver ConfirmarQuitarPickeoDialog). Mientras el
 * back no confirmó la unidad, spinner y sin tachito.
 */
function EstadoSeriePickeada({
  serie,
  pickeo,
  bloqueado = false,
  conTilde = true,
}: {
  serie: string;
  pickeo: UsePickeoLotesResult;
  /** Traza en curso o terminada: solo el tilde, sin tachito. */
  bloqueado?: boolean;
  /**
   * False en las series desplegadas de un lote: ahí solo se listan pickeadas,
   * así que el tilde no informa nada.
   */
  conTilde?: boolean;
}) {
  const [confirmar, setConfirmar] = useState(false);
  const unidad = pickeo.state.pickeos.find((x) => x.serie === serie);
  if (unidad?.stockId == null) {
    return <Loader2 className="ml-auto h-4 w-4 animate-spin text-gray-400" aria-label="Guardando" />;
  }
  const grupo = pickeo.grupos.find((g) => g.key === unidad.grupoKey);
  if (bloqueado) {
    return conTilde ? (
      <CheckCircle2 className="ml-auto h-4 w-4 text-green-600" aria-label="Pickeada" />
    ) : null;
  }
  return (
    <span className="inline-flex items-center justify-end gap-2">
      {conTilde && <CheckCircle2 className="h-4 w-4 text-green-600" aria-label="Pickeada" />}
      <button
        type="button"
        onClick={() => setConfirmar(true)}
        className="rounded text-red-600 hover:text-red-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
        title={`Quitar la serie ${serie} del pickeo`}
        aria-label={`Quitar la serie ${serie} del pickeo`}
      >
        <Trash2 className="h-4 w-4" />
      </button>
      <ConfirmarQuitarPickeoDialog
        open={confirmar}
        onOpenChange={setConfirmar}
        onConfirmar={() => pickeo.quitar(serie)}
        articulo={grupo?.nombre ?? grupo?.sku ?? null}
        lote={grupo?.lote ?? unidad.lectura.lote}
        serie={serie}
      />
    </span>
  );
}

/** Modal de confirmación del resync liviano (gln destino + GTIN, sin tocar stock). */
function ResincronizarDialog({
  open,
  onOpenChange,
  onConfirm,
  pending,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onConfirm: () => void;
  pending: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !pending && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Resincronizar despacho</DialogTitle>
          <DialogDescription className="text-xs">
            Se vuelven a consultar los datos del ERP y se actualizan en el sistema.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-gray-200 p-3 text-xs">
          <div className="mb-1 font-semibold text-gray-700">Se actualizarán:</div>
          <ul className="ml-4 list-disc space-y-0.5 text-gray-700">
            <li>
              <b>GLN destino</b> (del domicilio del cliente en el ERP)
            </li>
            <li>
              <b>Cliente</b> (nombre, datos)
            </li>
            <li>
              <b>GTIN</b> de los artículos (código de barras del ERP)
            </li>
            <li>
              <b>Depósito de origen</b> (nombre, por renglón)
            </li>
            <li>
              <b>Stock</b>: reconcilia contra el ERP fresco — da de alta las series
              nuevas y cancela las que ya no están.
            </li>
          </ul>
          <p className="mt-2 text-[11px] text-gray-500">
            Si una serie nueva ya está reservada en otro remito, no se toma sola:
            queda para reasignar con el botón.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancelar
          </Button>
          <Button
            onClick={onConfirm}
            disabled={pending}
            className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
          >
            {pending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-1 h-4 w-4" />
            )}
            {pending ? 'Resincronizando…' : 'Resincronizar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Modal para cargar/editar manualmente el GLN destino (13 dígitos). Se usa
 * cuando el ERP no tiene el barrio cargado en el domicilio del cliente. Guarda
 * en `erp_task.gln_destino` (la misma columna de la que lee la traza).
 */
function GlnDestinoDialog({
  open,
  onOpenChange,
  referencia,
  tipo,
  valorActual,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  referencia: string;
  tipo: ComprobanteTipo;
  valorActual: string | null;
}) {
  const [valor, setValor] = useState(valorActual ?? '');
  const mutation = useSetGlnDestinoMutation();

  // Resetear el input al valor actual cada vez que se abre el modal.
  useEffect(() => {
    if (open) setValor(valorActual ?? '');
  }, [open, valorActual]);

  const limpio = valor.trim();
  const valido = /^\d{13}$/.test(limpio);

  const handleGuardar = () => {
    if (!valido) return;
    mutation.mutate(
      { referencia, tipo, glnDestino: limpio },
      {
        onSuccess: () => {
          toast.success(`GLN destino guardado: ${limpio}`);
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(
            `No se pudo guardar: ${err instanceof Error ? err.message : String(err)}`,
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>GLN destino</DialogTitle>
          <DialogDescription className="text-xs">
            Cargá el GLN destino (13 dígitos). Se guarda en el sistema y es el que
            se declara al SNT al trazar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <input
            type="text"
            inputMode="numeric"
            value={valor}
            onChange={(e) => setValor(e.target.value.replace(/\D/g, '').slice(0, 13))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && valido && !mutation.isPending) handleGuardar();
            }}
            placeholder="Ej. 7793236000007"
            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm focus:border-everex-azul focus:outline-none"
            autoFocus
          />
          <p
            className={cn(
              'text-[11px]',
              limpio === '' || valido ? 'text-gray-500' : 'text-red-600',
            )}
          >
            {limpio === ''
              ? '13 dígitos numéricos.'
              : valido
                ? '✓ Formato válido.'
                : `Faltan ${13 - limpio.length} dígito(s) — deben ser 13.`}
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleGuardar}
            disabled={!valido || mutation.isPending}
            className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
          >
            {mutation.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Guardar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Sección "Trazar al SNT" — botón de acción + estado de la traza.
 *
 * Estados visuales:
 *   - Sin traza previa            → botón "Trazar" habilitado.
 *   - mutation.isPending          → spinner "Iniciando traza..."
 *   - traza.status pending/running → chip naranja "Trazando..."
 *   - traza.status error/failed    → chip rojo + mensaje + botón "Re-trazar"
 *
 * (El status 'success' no llega acá porque el backend filtra esas tareas
 * de la lista de pendientes — solo se ve si el usuario tiene esta vista
 * abierta justo cuando el poller la marca exitosa, edge case.)
 */
function TrazarSection({
  tarea,
  tipo,
  comprobante,
  deshabilitadoPorIntegridad = false,
  glnOrigen,
  glnDestino,
  tipoAgenteDestino,
  skusSinGtin = [],
  idEvento,
  eventoDesc,
  eventoBloqueoMsg = null,
  pickeoBloqueoMsg = null,
  pickeo,
}: {
  tarea: TareaPendiente;
  tipo: Exclude<ComprobanteTipo, never>;
  comprobante: ComprobanteDetalle;
  /** Cuando true, el botón Trazar queda disabled. La alerta de no-integridad
   *  se renderiza aparte (NoIntegridadAlert) — acá solo desactivamos la acción. */
  deshabilitadoPorIntegridad?: boolean;
  /** Remito por lote con el pickeo incompleto: motivo (del back) o null. */
  pickeoBloqueoMsg?: string | null;
  /** GLN de origen (código del GLN activo) — para el modal de confirmación. */
  glnOrigen: string | null;
  /** GLN destino guardado (erp_task.gln_destino). Null → bloquea la traza. */
  glnDestino: string | null;
  /** Tipo de actor SNT del GLN destino (para el modal de confirmación). */
  tipoAgenteDestino: string | null;
  /** SKU de renglones sin GTIN cargado en el ERP. Si hay alguno, bloquea la
   *  traza (riga rebota el ítem con error 3000). */
  skusSinGtin?: string[];
  /** event_code ANMAT elegido en el combo del encabezado (o null). Se manda
   *  en el payload de la traza. */
  idEvento: number | null;
  /** Descripción del evento elegido — se persiste como snapshot al trazar. */
  eventoDesc: string | null;
  /** null = se puede trazar (respecto del evento). String = motivo por el que
   *  NO se puede (cargando / error / sin eventos / no elegido). Deshabilita el
   *  botón y se muestra como alerta. El evento es obligatorio: sin uno válido no
   *  se traza (no hay default). */
  eventoBloqueoMsg?: string | null;
  /** Estado de pickeo por escaneo (vive en el padre; lo abre el botón "Pickear"). */
  pickeo: UsePickeoLotesResult;
}) {
  const mutation = useIniciarTrazaMutation();
  const cumplirMutation = useCumplirTareaMutation();
  const verificarMutation = useVerificarCambiosMutation();
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPickeo, setShowPickeo] = useState(false);
  const queryClient = useQueryClient();
  // Polling 1s a /api/traza/job — solo backend, NO toca riga. Se autopara
  // cuando el status entra en terminal (success/error/failed).
  const { data: trazaLive } = useTrazaJobByReferencia(tarea.referencia, 'egreso');

  // Al detectar la transición a 'success', invalidamos las queries dependientes
  // (listado de pendientes / completadas / stats) para que cuando el operador
  // vuelva al listado vea la fila ya sacada. El backend ya marcó `erp_task`
  // como 'trazada', pero TanStack tiene la lista cacheada con la fila vieja.
  const yaInvalidadoRef = useRef(false);
  useEffect(() => {
    if (trazaLive?.status === 'success' && !yaInvalidadoRef.current) {
      yaInvalidadoRef.current = true;
      void queryClient.invalidateQueries({ queryKey: ['tareas-pendientes'] });
      void queryClient.invalidateQueries({ queryKey: ['despacho-completadas'] });
      void queryClient.invalidateQueries({ queryKey: ['traza-stats'] });
      void queryClient.invalidateQueries({ queryKey: ['erp-tasks'] });
    }
  }, [trazaLive?.status, queryClient]);

  // El cierre en el ERP (cumplir-tarea) lo completa el backend en el mismo flujo
  // del poller, un instante DESPUÉS del success. `comprobante.cumplirTarea` sale
  // de `useComprobante`, que no pollea → sin esto el chip queda en "pendiente"
  // (amarillo) hasta re-entrar. Refrescamos el detalle hasta que el estado deje
  // de ser null (cumplida/error), y ahí pasa a verde/rojo solo. Cap de intentos
  // para no pollear infinito si el ERP no responde (queda el botón "Reintentar").
  const estadoCierreErp = comprobante.cumplirTarea?.estado ?? null;
  useEffect(() => {
    if (trazaLive?.status !== 'success' || estadoCierreErp != null) return;
    const refetch = () =>
      queryClient.invalidateQueries({
        queryKey: ['comprobante', tipo, tarea.referencia],
      });
    void refetch(); // inmediato
    let intentos = 0;
    const id = setInterval(() => {
      intentos += 1;
      void refetch();
      if (intentos >= 8) clearInterval(id); // ~24s
    }, 3000);
    return () => clearInterval(id);
  }, [trazaLive?.status, estadoCierreErp, queryClient, tipo, tarea.referencia]);

  // Prioridad: dato fresco del polling > resultado de la mutation > snapshot del listado.
  const trazaActual = trazaLive
    ? {
        jobId: trazaLive.jobId,
        status: trazaLive.status,
        errorMessage: trazaLive.errorMessage,
      }
    : mutation.data
      ? {
          jobId: mutation.data.jobId,
          status: mutation.data.status,
          errorMessage: null as string | null,
        }
      : tarea.traza
        ? {
            jobId: tarea.traza.jobId,
            status: tarea.traza.status,
            errorMessage: tarea.traza.errorMessage,
          }
        : null;

  const enCurso =
    trazaActual?.status === 'pending' || trazaActual?.status === 'running';
  const conError =
    trazaActual?.status === 'error' || trazaActual?.status === 'failed';
  // `anulada` con el remito anulado: se muestra la traza realizada con la
  // anulación. Anulada sin remito anulado = se vuelve a trazar (pendiente).
  const anuladaParaRetrazar =
    trazaActual?.status === 'anulada' && !comprobante.remitoAnulado;
  const exitosa =
    trazaActual?.status === 'success' ||
    (trazaActual?.status === 'anulada' && Boolean(comprobante.remitoAnulado));

  // Falta GLN destino: el despacho no tiene GLN destino guardado
  // (erp_task.gln_destino null). Puede ser porque el cliente no tenía el
  // `barrio` al sincronizar, o es una destrucción sin cliente. En cualquier
  // caso no se puede trazar (el backend también lo rechaza). Si el ERP ya
  // tiene el dato, el botón "Resincronizar" lo actualiza.
  const clienteIdComprobante = comprobante.comprobante.clienteId;
  const sinGlnDestino = !glnDestino;
  const faltaGtin = skusSinGtin.length > 0;

  const handleTrazar = () => {
    if (!tarea.referencia) return;
    if (!ubicacionActivaId) {
      toast.error('No hay GLN activo. Iniciá sesión y elegí un GLN antes de trazar.');
      return;
    }
    if (sinGlnDestino) {
      toast.error('No se puede trazar: falta el GLN destino.');
      return;
    }
    if (faltaGtin) {
      toast.error(
        `No se puede trazar: ${skusSinGtin.length} artículo(s) sin GTIN cargado en el ERP.`,
      );
      return;
    }
    if (eventoBloqueoMsg) {
      toast.error(eventoBloqueoMsg);
      return;
    }
    // Antes de confirmar: chequear contra ACSIS que el remito no haya cambiado
    // en el ERP. Si cambió, NO se puede trazar (los datos locales quedaron
    // viejos) → hay que resincronizar. Si está OK, abrimos la confirmación.
    verificarMutation.mutate(tarea.referencia, {
      onSuccess: (res) => {
        // 1) PRIORITARIO: anulado en el ERP (fechaBaja) → no se puede trazar.
        if (res.anulado) {
          const f = res.fechaBaja
            ? res.fechaBaja.slice(0, 10).split('-').reverse().join('/')
            : null;
          toast.error(
            `No se puede trazar: el remito está dado de baja (anulado) en el ERP${
              f ? ` el ${f}` : ''
            }.`,
          );
          return;
        }
        // 2) Cambió el set de partidas → hay que resincronizar.
        if (res.hayCambios) {
          const partes = [];
          if (res.agregadas > 0) partes.push(`${res.agregadas} agregada(s)`);
          if (res.removidas > 0) partes.push(`${res.removidas} removida(s)`);
          toast.error(
            `No se puede trazar: el remito cambió en el ERP (${partes.join(', ')}). ` +
              'Resincronizá el despacho antes de trazar.',
          );
          return;
        }
        setShowConfirm(true);
      },
      onError: (err) => {
        toast.error(`No se pudo verificar el remito contra el ERP: ${err.message}`);
      },
    });
  };

  // Traza real — se dispara desde el modal de confirmación.
  const confirmarTraza = () => {
    if (!tarea.referencia || !ubicacionActivaId) return;
    setShowConfirm(false);
    mutation.mutate(
      {
        referencia: tarea.referencia,
        tipo,
        glnId: ubicacionActivaId,
        erpId: tarea.id ?? null,
        idEvento,
        eventoDesc,
      },
      {
        onSuccess: (data) => {
          toast.success(`Traza iniciada · job_id ${data.jobId}`);
        },
        onError: (err) => {
          toast.error(`No se pudo iniciar la traza: ${err.message}`);
        },
      },
    );
  };

  const handleCumplirTarea = () => {
    if (!tarea.referencia) return;
    cumplirMutation.mutate(tarea.referencia, {
      onSuccess: (res) => {
        if (res.ok) {
          toast.success('Tarea cerrada en el ERP (cumplir-tarea OK)');
        } else if (res.skipped) {
          toast.error('No se pudo cerrar: la traza no tiene ID de tarea del ERP');
        } else {
          toast.error(
            `El ERP rechazó el cierre: ${res.mensajeError ?? res.error ?? 'ver audit trail'}`,
          );
        }
        // Refresca el detalle para que el chip de "Tarea informada al ERP"
        // muestre el estado actualizado (y el log del modal traiga la entry nueva).
        void queryClient.invalidateQueries({
          queryKey: ['comprobante', tipo, tarea.referencia],
        });
      },
      onError: (err) => {
        toast.error(`No se pudo cerrar la tarea: ${err.message}`);
      },
    });
  };

  return (
    <Card>
      {/* <CardHeader>
        <CardTitle className="text-everex-azul text-base">Trazar al SNT</CardTitle>
      </CardHeader> */}
      <CardContent className="space-y-3 mt-5">
        {/* Estado actual */}
        {exitosa ? (
          <Alert className="border-green-200 bg-green-50">
            <AlertDescription className="text-xs text-green-900">
              <b>Trazado con éxito</b>
            </AlertDescription>
          </Alert>
        ) : enCurso ? (
          <TrazaProgreso traza={trazaLive ?? null} />
        ) : anuladaParaRetrazar ? (
          <Alert>
            <AlertDescription className="text-xs">
              <b>Egreso anulado</b> · la traza anterior (ID del proceso #{trazaActual.jobId}) se
              anuló para volver a trazar este remito. Corregí el pickeo si hace falta y trazalo de
              nuevo.
            </AlertDescription>
          </Alert>
        ) : conError ? (
          <Alert variant="destructive">
            <AlertDescription className="text-xs">
              <b>Error de traza</b> · ID del proceso #{trazaActual.jobId}
              {trazaActual.errorMessage && (
                <div className="mt-1 font-mono text-[11px]">{trazaActual.errorMessage}</div>
              )}
            </AlertDescription>
          </Alert>
        ) : ('')}

        {/* Estado del cierre de tarea en el ERP (POST cumplir-tarea). Solo
            aplica una vez trazado. Si quedó en error, ofrece reintentar. */}
        {exitosa && (() => {
          const estadoCierre = comprobante.cumplirTarea?.estado ?? null;
          const enError = estadoCierre === 'error';
          const cumplida = estadoCierre === 'cumplida';
          return (
            <div
              className={cn(
                'flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3',
                enError
                  ? 'border-red-300 bg-red-50'
                  : cumplida
                    ? 'border-green-200 bg-green-50'
                    : 'border-amber-200 bg-amber-50',
              )}
            >
              <div>
                <div
                  className={cn(
                    'text-[10px] font-semibold uppercase tracking-wider',
                    enError ? 'text-red-700' : cumplida ? 'text-green-800' : 'text-amber-700',
                  )}
                >
                  {enError ? 'Tarea NO informada al ERP' : 'Tarea informada al ERP'}
                </div>
                <div className="mt-1">
                  <CumplirTareaChip
                    estado={estadoCierre}
                    fecha={comprobante.cumplirTarea?.fecha ?? null}
                    erpId={comprobante.erpId ?? tarea.id ?? null}
                  />
                </div>
              </div>
              {!cumplida && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCumplirTarea}
                  disabled={cumplirMutation.isPending}
                  className={cn(
                    'text-xs',
                    enError && 'border-red-300 text-red-700 hover:bg-red-100',
                  )}
                  title="Reintentar el cierre de la tarea en el ERP (POST cumplir-tarea)"
                >
                  {cumplirMutation.isPending ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : null}
                  Reintentar cierre en ERP
                </Button>
              )}
            </div>
          );
        })()}

        {/* No-integridad: alguna partida sin stock asociado (o reservada por
            otro remito). Va acá dentro del card, junto al resto de los bloqueos. */}
        {!exitosa && tarea.referencia && esIntegro(comprobante) === false && (
          <NoIntegridadAlert
            referencia={tarea.referencia}
            tipo={tipo}
            partidasSinStock={contarSinStock(comprobante)}
            totalPartidas={partidasConStockEsperado(comprobante).length}
          />
        )}

        {/* Partidas por lote: las series las aporta el pickeo, y la traza exige
            que esté completo (regla provisoria). Motivo informado por el back. */}
        {!exitosa && bloqueoPorPickeo(comprobante) && (
          <Alert>
            <AlertDescription className="text-xs">
              Este comprobante viene <b>agrupado por lote</b>: los números de serie se completan
              con el <b>pickeo</b>. {bloqueoPorPickeo(comprobante)}
            </AlertDescription>
          </Alert>
        )}

        {/* Alerta cuando el cliente no tiene GLN destino cargado en el ERP.
            Sin GLN destino el SNT productivo rechaza la traza; QA la deja pasar
            pero queda espúrea, así que la bloqueamos client-side (además de la
            validación del backend). */}
        {sinGlnDestino && !exitosa && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-destructive/50 p-4 text-xs text-destructive"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="leading-relaxed">
              <b>Falta GLN destino:</b>{' '}
              {clienteIdComprobante ? (
                <>
                  El cliente{' '}
                  <span className="font-mono">#{clienteIdComprobante}</span> no tiene
                  cargado el GLN destino en el domicilio del renglón{' '}
                  <b className="font-mono">
                    {comprobante.comprobante.renglonDomicilio ?? '—'}
                  </b>{' '}
                  (campo <code className="font-mono">barrio</code> del ERP).
                </>
              ) : (
                <>
                  El comprobante no tiene cliente/GLN destino asociado para informar al SNT.
                </>
              )}
            </div>
          </div>
        )}

        {/* Alerta cuando hay artículos sin GTIN cargado en el ERP. Sin GTIN
            riga rebota el ítem (error 3000), así que bloqueamos la traza. */}
        {faltaGtin && !exitosa && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-destructive/50 p-4 text-xs text-destructive"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="leading-relaxed">
              <b>Falta GTIN.</b> {skusSinGtin.length} artículo(s) sin código de barras
              cargado en el ERP{' '}
              <span className="font-mono">(SKU: {skusSinGtin.join(', ')})</span>. Cargá el
              GTIN antes de trazar.
            </div>
          </div>
        )}

        {/* Bloqueo por evento SNT: cargando / error / sin eventos disponibles /
            no elegido. El evento es obligatorio — sin uno válido NO se traza
            (no hay default). El motivo lo computa el padre. */}
        {eventoBloqueoMsg && !exitosa && !sinGlnDestino && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-4 text-xs text-amber-800"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="leading-relaxed">
              <b>No se puede trazar todavía:</b> {eventoBloqueoMsg}
            </div>
          </div>
        )}

        {/* Botones — habilitados solo si no hay traza en curso ni exitosa */}
        {!exitosa && (
          <div className="flex items-center gap-2">
            {/* Pickeo por escaneo: independiente de los bloqueos de la traza.
                Solo requiere que el comprobante tenga partidas con serie. */}
            {PICKEO_HABILITADO && (
            <Button
              variant="outline"
              onClick={() => setShowPickeo(true)}
              disabled={pickeo.resumen.esperadas === 0}
              title={
                pickeo.resumen.esperadas === 0
                  ? 'El comprobante no tiene unidades con número de serie para pickear.'
                  : 'Escanear las unidades del despacho y marcarlas en la lista'
              }
              className="border-everex-azul text-everex-azul hover:bg-everex-azul-bg"
            >
              <ScanLine className="mr-2 h-4 w-4" />
              Pickear
              {pickeo.resumen.pickeadas > 0 && (
                <span className="ml-1 text-xs opacity-80">
                  ({pickeo.resumen.pickeadas}/{pickeo.resumen.esperadas})
                </span>
              )}
            </Button>
            )}
            <Button
              onClick={handleTrazar}
              disabled={
                mutation.isPending ||
                verificarMutation.isPending ||
                enCurso ||
                deshabilitadoPorIntegridad ||
                Boolean(pickeoBloqueoMsg) ||
                sinGlnDestino ||
                faltaGtin ||
                Boolean(eventoBloqueoMsg)
              }
              className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
              title={
                deshabilitadoPorIntegridad
                  ? 'No se puede trazar: el comprobante no es íntegro. Resincronizá el item primero.'
                  : pickeoBloqueoMsg
                    ? `No se puede trazar: ${pickeoBloqueoMsg}`
                    : sinGlnDestino
                    ? 'No se puede trazar: el cliente no tiene GLN destino cargado en el ERP.'
                    : faltaGtin
                      ? 'No se puede trazar: hay artículos sin GTIN cargado en el ERP.'
                      : (eventoBloqueoMsg ?? undefined)
              }
            >
              {mutation.isPending || verificarMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Zap className="mr-2 h-4 w-4" />
              )}
              {conError ? 'Re-trazar' : 'Trazar'}
            </Button>
            {verificarMutation.isPending && (
              <span className="text-xs text-gray-600">Verificando remito contra el ERP…</span>
            )}
            {mutation.isPending && (
              <span className="text-xs text-gray-600">Iniciando traza...</span>
            )}
            {/* Debug: previsualiza el JSON que se le enviaría a riga-traza sin
                disparar la traza real. NO toca riga ni la DB. */}
            {tarea.referencia && ubicacionActivaId && (
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                className="ml-auto text-[11px] text-gray-500 underline-offset-2 transition hover:text-everex-azul hover:underline"
                title="Ver el JSON que se le enviaría a riga-traza (debug, no manda nada)"
              >
                (debug: ver payload)
              </button>
            )}
          </div>
        )}

        {tarea.referencia && ubicacionActivaId && (
          <PayloadPreviewDialog
            open={showPreview}
            onOpenChange={setShowPreview}
            referencia={tarea.referencia}
            tipo={tipo}
            glnId={ubicacionActivaId}
            idEvento={idEvento}
          />
        )}

        {/* Pickeo por escaneo — estado en el padre, se conserva al cerrar. */}
        {PICKEO_HABILITADO && (
          <PickeoModal
            open={showPickeo}
            onOpenChange={setShowPickeo}
            pickeo={pickeo}
            permiteQuitar={!exitosa && !enCurso}
            numeroAfip={comprobante.numeroAfip}
            referencia={tarea.referencia}
          />
        )}

        {/* Confirmación antes de informar a ANMAT: origen → destino (+ tipo de
            actor) y el evento elegido. */}
        <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-everex-azul">Confirmar traza al SNT</DialogTitle>
              <DialogDescription className="text-xs">
                Revisá los datos antes de informar a ANMAT.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Origen → Destino */}
              <div className="flex items-center justify-center gap-4">
                <div className="text-center">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    GLN Origen
                  </div>
                  <div className="mt-1 font-mono text-sm text-gray-900">{glnOrigen ?? '—'}</div>
                </div>
                <ArrowRight className="h-6 w-6 shrink-0 text-everex-naranja" />
                <div className="text-center">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    Destino
                  </div>
                  <div className="mt-1 font-mono text-sm text-gray-900">{glnDestino ?? '—'}
                  {tipoAgenteDestino && (
                    <div className="ml-1 mt-1 inline-block rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                      {tipoAgenteDestino}
                    </div>
                  )}
                  </div>
                </div>
              </div>

              {/* Evento */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-center">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  Evento SNT
                </div>
                <div className="mt-1 text-sm text-gray-900">
                  <span className="font-mono">{idEvento ?? '—'}</span>
                  {eventoDesc ? <span className="text-gray-600"> — {eventoDesc}</span> : null}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirm(false)}>
                Cancelar
              </Button>
              <Button
                onClick={confirmarTraza}
                disabled={mutation.isPending}
                className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
              >
                {mutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Zap className="mr-2 h-4 w-4" />
                )}
                Trazar al SNT
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detalle completo de trazabilidad — solo cuando la traza llegó a un
            estado TERMINAL (success / error / failed / anulada). Mientras está
            en curso (pending / running) NO mostramos el desglose: los contadores
            y transacciones se estabilizan recién al final, y el botón "Descargar
            PDF" no aplica para una traza incompleta. Una anulada se muestra sin
            "Cancelar egreso" ni PDF. */}
        {trazaLive &&
          (trazaLive.status === 'success' ||
            trazaLive.status === 'error' ||
            trazaLive.status === 'failed' ||
            trazaLive.status === 'anulada') && (
            <DetalleTrazabilidad traza={trazaLive} comprobante={comprobante} />
          )}
      </CardContent>
    </Card>
  );
}

/** "2026-06-05T19:01:42.000Z" → "05/06/2026 16:01" (local). */
function fmtFechaHoraLocal(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

/**
 * Detalle completo de la traza: atribución, contadores, códigos ANMAT, y la
 * lista de transacciones (chunks) con los items y el error individual de
 * cada uno si lo hay.
 */
function DetalleTrazabilidad({
  traza,
  comprobante,
}: {
  traza: TrazaJobDetalle;
  comprobante: ComprobanteDetalle;
}) {
  const transacciones = traza.resultPayload?.transacciones ?? [];

  // Usuario logueado EN VIVO — es quien imprime el PDF ahora (distinto del que
  // trazó, que es `traza.usuario`). Se muestra en el pie del reporte.
  const usuarioActual = useSessionStore((s) => s.usuario);

  // GLN destino: de la DB (`comprobante.glnDestino`, persistido en el sync) —
  // NO se deriva del cliente en vivo. La dirección del domicilio sí sale del
  // cliente online (solo para mostrar, no es crítica); React Query comparte el
  // caché con la card de Cliente, así que no hay request extra.
  const { data: cliente } = useClienteAcsis(comprobante.comprobante.clienteId);
  const domicilioDestino = cliente?.domicilios.find(
    (d) => d.renglon === comprobante.comprobante.renglonDomicilio,
  );
  const glnDestino = comprobante.glnDestino ?? null;

  const handleDescargarPdf = () => {
    void (async () => {
      try {
        await generarPdfTrazaJob({
          traza,
          comprobante,
          impresoPor: usuarioActual?.nombre ?? null,
        });
      } catch (err) {
        toast.error(
          `No se pudo generar el PDF: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    })();
  };

  const [showPayloads, setShowPayloads] = useState(false);

  // Anulación del egreso (NO cancela en ANMAT): el diálogo decide qué pasa con
  // las unidades. "Cancelar remito" (NO cancela en el ERP) queda para egresos
  // anulados con el flujo anterior, que dejaban el remito sin anular.
  const egresoAnulado = comprobante.egresoAnulado ?? null;
  const remitoAnulado = comprobante.remitoAnulado ?? null;
  // Tarea cerrada en Plataforma: se puede anular el egreso/remito (si se canceló
  // en ANMAT las series tienen que poder volver a salir), pero no re-trazarlo.
  const cerradaEnErp = comprobante.cumplirTarea?.estado === 'cumplida';
  const cancelarEgresoMut = useCancelarEgresoMutation();
  const cancelarRemitoMut = useCancelarRemitoMutation();
  const [showCancelEgreso, setShowCancelEgreso] = useState(false);
  const [showCancelRemito, setShowCancelRemito] = useState(false);

  const handleCancelarEgreso = (destino: DestinoAnulacionEgreso) => {
    cancelarEgresoMut.mutate(
      { referencia: comprobante.referencia, tipo: comprobante.tipo, destino },
      {
        onSuccess: (r) => {
          const que =
            r.destino === 'retrazar'
              ? 'siguen pickeadas y el remito volvió a Pendientes para trazarlo de nuevo'
              : r.destino === 'liberar'
                ? 'quedaron disponibles y el remito quedó anulado'
                : 'quedaron pendientes de reingreso y el remito quedó anulado';
          toast.success(`Egreso anulado · ${r.revertidas} serie(s) ${que}.`);
          setShowCancelEgreso(false);
        },
        onError: (err) =>
          toast.error(
            `No se pudo cancelar el egreso: ${err instanceof Error ? err.message : String(err)}`,
          ),
      },
    );
  };

  const handleCancelarRemito = () => {
    cancelarRemitoMut.mutate(
      { referencia: comprobante.referencia, tipo: comprobante.tipo },
      {
        onSuccess: (r) => {
          toast.success(`Remito anulado · ${r.liberadas} serie(s) liberadas a inventario libre.`);
          setShowCancelRemito(false);
        },
        onError: (err) =>
          toast.error(
            `No se pudo cancelar el remito: ${err instanceof Error ? err.message : String(err)}`,
          ),
      },
    );
  };

  return (
    <div className="mt-4 space-y-4 border-t border-gray-200 pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-everex-azul">
            Detalle de trazabilidad
          </span>
          <span className="text-[11px] text-gray-600">
            RIGA-Traza ID: <b className="font-mono text-gray-800">#{traza.jobId}</b>
            <button
              type="button"
              onClick={() => setShowPayloads(true)}
              className="ml-2 text-gray-500 underline-offset-2 transition hover:text-everex-azul hover:underline"
              title="Ver los JSON intercambiados con riga-traza (request + initial + result)"
            >
              (ver log)
            </button>
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {traza.status === 'success' && !egresoAnulado && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCancelEgreso(true)}
              className="border-red-300 text-xs text-red-700 hover:bg-red-50"
            >
              Cancelar egreso
            </Button>
          )}
          {egresoAnulado && !remitoAnulado && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCancelRemito(true)}
              className="border-red-300 text-xs text-red-700 hover:bg-red-50"
            >
              Cancelar remito
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleDescargarPdf}
            disabled={Boolean(egresoAnulado) || traza.status === 'anulada'}
            className="text-xs"
            title={
              egresoAnulado || traza.status === 'anulada'
                ? 'No se puede imprimir: el egreso de este despacho está anulado.'
                : 'Descargar comprobante de trazabilidad en PDF'
            }
          >
            <FileDown className="mr-1.5 h-3.5 w-3.5" />
            Descargar PDF
          </Button>
        </div>
      </div>

      {cerradaEnErp && traza.status === 'success' && (
        <p className="text-[11px] text-gray-600">
          La tarea está <b>cerrada en Plataforma</b>: el despacho no se puede modificar ni volver a
          trazar. Si se canceló en ANMAT, podés cancelar el egreso para liberar las unidades.
        </p>
      )}

      {(egresoAnulado || remitoAnulado) && (
        <Alert variant="destructive">
          <AlertDescription className="text-xs">
            <b>Despacho anulado.</b>
            {egresoAnulado && (
              <>
                {' '}Egreso anulado el {fmtFechaHoraLocal(egresoAnulado.at)}
                {egresoAnulado.por ? ` por ${egresoAnulado.por}` : ''}.
              </>
            )}
            {remitoAnulado && (
              <>
                {' '}Remito anulado el {fmtFechaHoraLocal(remitoAnulado.at)}
                {remitoAnulado.por ? ` por ${remitoAnulado.por}` : ''}.
              </>
            )}
          </AlertDescription>
        </Alert>
      )}

      <CancelarEgresoDialog
        open={showCancelEgreso}
        onOpenChange={setShowCancelEgreso}
        transacciones={traza.codigosTransaccion}
        remito={comprobante.numeroAfip ?? comprobante.referencia}
        pending={cancelarEgresoMut.isPending}
        permitirRetrazar={!cerradaEnErp}
        onConfirmar={handleCancelarEgreso}
      />

      {/* Cancelar remito (r/p → a) para egresos anulados con el flujo anterior.
          Avisa que el ERP se cancela a mano. */}
      <Dialog
        open={showCancelRemito}
        onOpenChange={(o) => !cancelarRemitoMut.isPending && setShowCancelRemito(o)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Cancelar remito</DialogTitle>
            <DialogDescription className="text-xs">
              Esto <b>libera las series</b> a inventario libre (quedan disponibles para
              otro remito). <b>NO cancela el remito en el ERP</b>: la cancelación del
              remito <b className="font-mono">{comprobante.numeroAfip ?? comprobante.referencia}</b>{' '}
              debe hacerse manualmente en el ERP.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowCancelRemito(false)}
              disabled={cancelarRemitoMut.isPending}
            >
              Volver
            </Button>
            <Button
              onClick={handleCancelarRemito}
              disabled={cancelarRemitoMut.isPending}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {cancelarRemitoMut.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              Cancelar remito
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <PayloadsDialog
        open={showPayloads}
        onOpenChange={setShowPayloads}
        traza={traza}
      />

      {/* Resumen atribuible */}
      <div className="grid grid-cols-2 gap-4 text-xs md:grid-cols-4">
        <Dato label="Fecha trazado">{fmtFechaHoraLocal(traza.finishedAt ?? traza.createdAt)}</Dato>
        <Dato label="Usuario">{traza.usuario.nombre}</Dato>
        <Dato label="GLN origen">
          {traza.gln.nombre}{' '}
          <span className="text-[10px] text-gray-500">({traza.gln.gln})</span>
        </Dato>
        <Dato label="GLN destino">
          {glnDestino ? (
            <>
              <span className="font-mono">{glnDestino}</span>
              {domicilioDestino?.direccion && (
                <span className="block text-[10px] text-gray-500">
                  {domicilioDestino.direccion}
                </span>
              )}
            </>
          ) : (
            <span className="text-gray-400 italic">sin configurar</span>
          )}
        </Dato>
        <Dato label="N° Remito enviado" mono>
          {traza.numeroRemito ?? '—'}
        </Dato>
        <Dato label="Items">
          {traza.doneItems ?? '—'}
          {traza.totalItems != null && (
            <span className="text-gray-500"> / {traza.totalItems}</span>
          )}
          {traza.errorItems != null && traza.errorItems > 0 && (
            <span className="ml-1 text-red-700">({traza.errorItems} con error)</span>
          )}
        </Dato>
      </div>

      {/* Códigos de transacción ANMAT */}
      {traza.codigosTransaccion.length > 0 && (
        <div>
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            Códigos de transacción ANMAT ({traza.codigosTransaccion.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {traza.codigosTransaccion.map((c) => (
              <span
                key={c}
                className="rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700"
                title="Código de transacción ANMAT"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Transacciones individuales (chunks) */}
      {transacciones.length > 0 && (
        <div>
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            Transacciones ({transacciones.length})
          </div>
          <div className="overflow-x-auto rounded border border-gray-200">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
                <tr>
                  <th className="px-2 py-1.5 text-left">#</th>
                  <th className="px-2 py-1.5 text-left">Estado</th>
                  <th className="px-2 py-1.5 text-left">Código ANMAT</th>
                  <th className="px-2 py-1.5 text-left">Items</th>
                  <th className="px-2 py-1.5 text-left">Error</th>
                </tr>
              </thead>
              <tbody className="text-gray-800">
                {transacciones.map((t, i) => {
                  const items = Array.isArray(t.items) ? t.items : [];
                  const success = t.status === 'success';
                  // Si hubo error, el `codigoTransaccion` que venga es espúreo
                  // (no representa un código ANMAT válido) — no lo mostramos,
                  // mismo criterio que `traza_job_detail.anmat_transaction_id`.
                  const conError = typeof t.error === 'string' && t.error.length > 0;
                  const anmatId = conError ? null : t.codigoTransaccion;
                  return (
                    <tr key={`${t.codigoTransaccion ?? 'sin-cod'}-${i}`} className="border-t border-gray-100 align-top">
                      <td className="px-2 py-1.5 text-gray-500">{i + 1}</td>
                      <td className="px-2 py-1.5">
                        <span
                          className={cn(
                            'rounded px-1.5 py-0.5 text-[10px] font-semibold',
                            success
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800',
                          )}
                        >
                          {t.status ?? '—'}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 font-mono">{anmatId ?? '—'}</td>
                      <td className="px-2 py-1.5">
                        {items.length === 0 ? (
                          <span className="text-gray-400">—</span>
                        ) : (
                          <ul className="space-y-0.5">
                            {items.map((it, j) => (
                              <li key={j} className="font-mono text-[11px]">
                                {it.gtin ?? '?'} · L{it.lote ?? '?'} · serie {it.numero_serial ?? '?'}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-[11px] text-red-700">
                        {t.error || <span className="text-gray-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Modal con los 3 JSON intercambiados con riga-traza:
 *   - REQUEST: lo que mandamos al POST /api/out/trazar-async
 *   - INITIAL RESPONSE: lo que riga respondió de inmediato (job_id + total_chunks)
 *   - RESULT: el `result` final cuando el job terminó (con transacciones + códigos ANMAT)
 */
function PayloadsDialog({
  open,
  onOpenChange,
  traza,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  traza: TrazaJobDetalle;
}) {
  const fmt = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return '[no serializable]';
    }
  };

  const copiar = (label: string, value: unknown) => {
    void (async () => {
      try {
        await navigator.clipboard.writeText(fmt(value));
        toast.success(`${label} copiado al portapapeles`);
      } catch {
        toast.error('No se pudo copiar (¿el navegador bloqueó el portapapeles?)');
      }
    })();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-everex-azul">
            JSON intercambiado con riga-traza · #{traza.jobId}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Mensajes crudos guardados en <code>traza_job</code>. Útil para auditoría
            regulatoria y diagnóstico de errores ANMAT.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto pr-1" style={{ maxHeight: '65vh' }}>
          <PayloadBloque
            label="Request → riga"
            descripcion="Body del POST /api/out/trazar-async"
            value={traza.requestPayload}
            onCopiar={copiar}
          />
          <PayloadBloque
            label="Initial response ← riga"
            descripcion="Respuesta inmediata del async (devuelve job_id + total_chunks)"
            value={traza.initialResponse}
            onCopiar={copiar}
          />
          <PayloadBloque
            label="Result final ← riga"
            descripcion="`result` cuando el job terminó: transacciones, código ANMAT, errores por chunk"
            value={traza.resultPayload}
            onCopiar={copiar}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PayloadBloque({
  label,
  descripcion,
  value,
  onCopiar,
}: {
  label: string;
  descripcion: string;
  value: unknown;
  onCopiar: (label: string, value: unknown) => void;
}) {
  const json = value === null || value === undefined ? '—' : JSON.stringify(value, null, 2);
  const vacio = value === null || value === undefined;
  return (
    <div className="rounded border border-gray-200 bg-gray-50">
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-white px-3 py-2">
        <div>
          <div className="text-xs font-bold text-everex-azul">{label}</div>
          <div className="text-[10px] text-gray-600">{descripcion}</div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onCopiar(label, value)}
          disabled={vacio}
          className="h-7 text-[11px]"
        >
          Copiar
        </Button>
      </div>
      <pre className="overflow-x-auto px-3 py-2 text-[11px] font-mono leading-tight text-gray-800">
        {json}
      </pre>
    </div>
  );
}

/**
 * Card de "Cliente / Destino" — datos del cliente ACSIS y sus domicilios.
 *
 * Pinta nombre legal, CUIT, tipo cliente y Tipo agente SNT (clasificador2 de
 * ACSIS, hoy null en producción hasta que lo configuren). Lista los
 * domicilios y resalta la fila cuyo `renglon` matchea con el
 * `renglonDomicilio` del comprobante — ése es el GLN destino del despacho.
 */
/**
 * Cuerpo de la card de Cliente — encabezado del cliente + UN solo domicilio,
 * el que matchea con `renglonDomicilio` del comprobante. Si por algún motivo
 * el cliente no tiene ese renglón cargado en `domicilios[]`, lo decimos
 * explícitamente para que se note que hay un dato inconsistente en ACSIS.
 */
function ClienteSectionBody({
  cliente,
  renglonDomicilio,
}: {
  cliente: import('@/api/contracts/cliente').Cliente;
  renglonDomicilio: string | null;
}) {
  const domicilio = renglonDomicilio
    ? cliente.domicilios.find((d) => d.renglon === renglonDomicilio)
    : undefined;

  return (
    <div className="space-y-4">
      {/* Datos del cliente */}
      <div className="grid grid-cols-2 gap-4 text-xs md:grid-cols-4">
        <Dato label="Nombre legal">
          {cliente.nombreLegal ?? cliente.nombre ?? '—'}
        </Dato>
        <Dato label="CUIT" mono>
          {cliente.cuit ?? '—'}
        </Dato>
        <Dato label="Tipo cliente">{cliente.tipoCliente ?? '—'}</Dato>
        {/* "Tipo agente SNT" se muestra arriba (header del comprobante), no lo
            repetimos acá. */}
      </div>

      {/* Domicilio del remito */}
      <div>
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
          Domicilio del remito{' '}
          {renglonDomicilio && (
            <span className="text-gray-400">(renglón {renglonDomicilio})</span>
          )}
        </div>
        {!domicilio ? (
          <Alert>
            <AlertDescription className="text-xs">
              {renglonDomicilio ? (
                <>
                  El cliente no tiene cargado el renglón{' '}
                  <b className="font-mono">{renglonDomicilio}</b> en sus
                  domicilios — verificar en el ERP.
                </>
              ) : (
                <>El remito no especifica renglón de domicilio.</>
              )}
            </AlertDescription>
          </Alert>
        ) : (
          <div className="grid grid-cols-2 gap-4 text-xs md:grid-cols-4">
            <Dato label="Dirección">{domicilio.direccion ?? '—'}</Dato>
            <Dato label="Localidad">{domicilio.localidad ?? '—'}</Dato>
            <Dato label="Provincia">{domicilio.provincia ?? '—'}</Dato>
            <Dato label="Código postal">{domicilio.codigoPostal ?? '—'}</Dato>
          </div>
        )}
      </div>
    </div>
  );
}

function ClienteSection({
  clienteId,
  renglonDomicilio,
}: {
  clienteId: string;
  renglonDomicilio: string | null;
}) {
  const { data: cliente, isLoading, error } = useClienteAcsis(clienteId);
  const [showClienteLog, setShowClienteLog] = useState(false);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-everex-azul text-base">
          Cliente / Destino{' '}
          <span className="font-mono text-sm text-gray-500">#{clienteId}</span>
          {cliente?.raw !== undefined && (
            <button
              type="button"
              onClick={() => setShowClienteLog(true)}
              className="ml-2 font-sans text-[10px] font-normal text-gray-500 underline-offset-2 transition hover:text-everex-azul hover:underline"
              title="Ver el JSON crudo devuelto por el ERP"
            >
              (ver log)
            </button>
          )}
        </CardTitle>
        <p className="text-[11px] text-gray-600">
          Datos maestros del cliente en el ERP
        </p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-6 text-gray-600">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            <span className="text-xs">Cargando datos del cliente…</span>
          </div>
        ) : error ? (
          // No es un dato crítico para trazar (el GLN ya viene de la DB). Si el
          // ERP no responde, avisamos suave y el resto del despacho se ve igual.
          <Alert>
            <AlertDescription className="text-xs text-gray-600">
              Información del cliente no disponible — no se pudo consultar el ERP.
              El resto del despacho no se ve afectado.
            </AlertDescription>
          </Alert>
        ) : cliente ? (
          <ClienteSectionBody cliente={cliente} renglonDomicilio={renglonDomicilio} />
        ) : null}
      </CardContent>

      <ClienteRawDialog
        open={showClienteLog}
        onOpenChange={setShowClienteLog}
        clienteId={clienteId}
        raw={cliente?.raw}
      />
    </Card>
  );
}

/**
 * Modal con el JSON crudo del cliente devuelto por el ERP. Muestra `cliente.raw`
 * (el envelope completo de ACSIS: `{ response: {...}, status, ... }`).
 */
function ClienteRawDialog({
  open,
  onOpenChange,
  clienteId,
  raw,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clienteId: string;
  raw: unknown;
}) {
  const handleCopiar = () => {
    void navigator.clipboard.writeText(JSON.stringify(raw, null, 2));
    toast.success('JSON del cliente copiado al portapapeles');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-everex-azul">
            Response ERP · Cliente #{clienteId}
          </DialogTitle>
          <DialogDescription className="text-xs">
            JSON crudo que devolvió el ERP al consultar el maestro del cliente.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto" style={{ maxHeight: '65vh' }}>
          {raw === undefined ? (
            <Alert>
              <AlertDescription className="text-xs">
                No hay log disponible para este cliente.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="rounded border border-gray-200 bg-gray-50">
              <div className="flex items-center justify-end border-b border-gray-200 bg-white px-3 py-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopiar}
                  className="h-7 text-[11px]"
                >
                  Copiar
                </Button>
              </div>
              <pre className="overflow-x-auto px-3 py-2 text-[11px] font-mono leading-tight text-gray-800">
                {JSON.stringify(raw, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Modal de debug "ver payload" — muestra el JSON que se le enviaría a
 * riga-traza-v2 para esta combinación de (referencia, tipo, glnId). NO toca
 * riga ni la DB: el backend lo arma y lo devuelve. Lazy: la request se
 * dispara solo cuando se abre el modal.
 */
function PayloadPreviewDialog({
  open,
  onOpenChange,
  referencia,
  tipo,
  glnId,
  idEvento,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  referencia: string;
  tipo: ComprobanteTipo;
  glnId: string | number;
  /** Evento elegido en el combo — para que el preview muestre el mismo que se
   *  va a mandar (si es null, el backend previsualiza con el default). */
  idEvento: number | null;
}) {
  const { data, isLoading, error } = usePreviewPayloadEgreso(
    referencia,
    tipo,
    glnId,
    idEvento,
    open,
  );

  const handleCopiar = () => {
    void (async () => {
      try {
        await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
        toast.success('Payload copiado al portapapeles');
      } catch {
        toast.error('No se pudo copiar (¿el navegador bloqueó el portapapeles?)');
      }
    })();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-everex-azul">
            Payload a riga-traza · {referencia}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Vista previa del JSON que se enviaría al <code className="font-mono text-[10px]">POST /api/out/trazar-async</code> de
            riga-traza-v2. <b>NO se está mandando nada</b> — es debug puro
            (no toca riga ni la DB).
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto" style={{ maxHeight: '65vh' }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-gray-600">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              <span className="text-xs">Armando payload...</span>
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertDescription className="text-xs">
                No se pudo armar el payload:{' '}
                {error instanceof Error ? error.message : String(error)}
              </AlertDescription>
            </Alert>
          ) : data !== undefined ? (
            <div className="rounded border border-gray-200 bg-gray-50">
              <div className="flex items-center justify-end border-b border-gray-200 bg-white px-3 py-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopiar}
                  className="h-7 text-[11px]"
                >
                  Copiar
                </Button>
              </div>
              <pre className="overflow-x-auto px-3 py-2 text-[11px] font-mono leading-tight text-gray-800">
                {JSON.stringify(data, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Modal del "ver log" de ACSIS — muestra el JSON crudo devuelto por el ERP
 * para esta referencia + tipo. La query es lazy (solo se dispara cuando el
 * modal está abierto) para no consumir banda si nadie lo abre.
 */
function AcsisRawDialog({
  open,
  onOpenChange,
  referencia,
  tipo,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  referencia: string;
  tipo: ComprobanteTipo;
}) {
  const { data, isLoading, error } = useComprobanteRaw(referencia, tipo, open);

  const handleCopiar = () => {
    void (async () => {
      try {
        await navigator.clipboard.writeText(JSON.stringify(data?.response, null, 2));
        toast.success('JSON copiado al portapapeles');
      } catch {
        toast.error('No se pudo copiar (¿el navegador bloqueó el portapapeles?)');
      }
    })();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-everex-azul">
            Response ERP · {referencia}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Snapshot del JSON que devolvió el ERP al sincronizar.
            {data?.erpId && (
              <>
                {' '}
                ID (ERP): <code className="font-mono text-[10px]">{data.erpId}</code>
              </>
            )}
            {data?.importadoAt && (
              <>
                {' · Importado: '}
                <code className="font-mono text-[10px]">
                  {fmtFechaHora(data.importadoAt)}
                </code>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto" style={{ maxHeight: '65vh' }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-gray-600">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              <span className="text-xs">Cargando snapshot...</span>
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertDescription className="text-xs">
                No se pudo traer el log:{' '}
                {error instanceof Error ? error.message : String(error)}
              </AlertDescription>
            </Alert>
          ) : data ? (
            <div className="rounded border border-gray-200 bg-gray-50">
              <div className="flex items-center justify-end border-b border-gray-200 bg-white px-3 py-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopiar}
                  className="h-7 text-[11px]"
                >
                  Copiar
                </Button>
              </div>
              <pre className="overflow-x-auto px-3 py-2 text-[11px] font-mono leading-tight text-gray-800">
                {JSON.stringify(data.response, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Dato({
  label,
  children,
  mono,
  className,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className={cn('mt-0.5 text-gray-800', mono && 'font-mono')}>{children}</div>
    </div>
  );
}

/**
 * Alerta que se muestra cuando el comprobante no es íntegro (≥1 partida
 * sin stock asociado). Bloquea la traza y ofrece resincronizar el item.
 */
function NoIntegridadAlert({
  referencia,
  tipo,
  partidasSinStock,
  totalPartidas,
}: {
  referencia: string;
  tipo: ComprobanteTipo;
  partidasSinStock: number;
  totalPartidas: number;
}) {
  // Única vía ante no-integridad: tomar stock que esté en inventario libre
  // (`a=1`) — típicamente liberado por una anulación. "Reasignar" (mover de otro
  // remito) se ocultó por peligroso; el camino correcto es anular + esto.
  const asignarMutation = useAsignarDisponiblesMutation();

  const handleAsignar = () => {
    asignarMutation.mutate(
      { referencia, tipo },
      {
        onSuccess: (r) => {
          if (r.asignadas > 0) {
            toast.success(`Asignada(s) ${r.asignadas} serie(s) disponible(s) a este remito.`);
          } else {
            toast.error(
              'No hay series disponibles para asignar. Si estas unidades estaban en otro remito, primero anulá ese despacho (Cancelar egreso + Cancelar remito).',
            );
          }
        },
        onError: (err) => {
          toast.error(
            `No se pudieron asignar: ${err instanceof Error ? err.message : String(err)}`,
          );
        },
      },
    );
  };

  return (
    <Alert variant="destructive">
      <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs">
          Este comprobante <b>no puede ser trazado</b>: {partidasSinStock} de {totalPartidas} partida
          {totalPartidas === 1 ? '' : 's'} no tiene stock asociado. Si las unidades ya
          están <b>disponibles</b> (liberadas por una anulación), tomalas con
          {' '}<b>Asignar disponibles</b>. Si no, <b>Resincronizá</b> (arriba) para
          actualizar el stock desde el ERP.
        </span>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button
            size="sm"
            onClick={handleAsignar}
            disabled={asignarMutation.isPending}
            className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
          >
            {asignarMutation.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : null}
            Asignar disponibles
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
